import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  AgxLabel,
  AgxRow,
  AgxColumn,
  AgxTextInput,
  AgxSegmentedPicker,
  AgxSelect,
  DepositPayable,
} from '@urbanx/agx-ui-components';
import {
  getPayableSegments,
  DAYS_BUSINESSDAYS_OPTIONS,
} from 'components/common/Payable';
import { Breakpoints, ScreenSize } from 'utils/screen';
import { parseStringToNumber } from 'utils/numbers';
import { ErrorState } from 'types/Payable';
import {
  AgxDatePicker,
  AgxSatisfactionOfSpecialCondition,
  AgxSegmentPicker,
} from '@urbanx/agx.ui-components2';
import {
  FormCondition,
  Condition,
  ConditionType,
  DayType,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';

interface InspectionOfRecordsProps {
  id: string;
  onValueChanged: (change: { id: string; value: FormCondition }) => void;
  defaultValue: FormCondition;
  validate?: boolean;
  required?: boolean;
  error?: ErrorState;
  specialConditions: SpecialConditionData[];
  campaignId: string;
}

const segments = getPayableSegments(false);

const InspectionOfRecords = ({
  id,
  defaultValue,
  onValueChanged,
  validate = false,
  specialConditions,
  campaignId,
}: InspectionOfRecordsProps) => {
  const [selectedCondition, setSelectedCondition] = useState<Condition>(
    defaultValue?.condition ?? Condition.NotApplicable
  );

  const [inspectionOfRecordsDetails, setInspectionOfRecordsDetails] =
    useState<FormCondition>({
      ...defaultValue,
      dayType: defaultValue?.dayType ?? DayType.Days,
      depositPayable: defaultValue?.depositPayable ?? DepositPayable.NDaysFrom,
    });

  const [selectedSegment, setSelectedSegment] = useState<DepositPayable>(
    inspectionOfRecordsDetails?.depositPayable ??
      (inspectionOfRecordsDetails?.condition === Condition.NdaysFromDate
        ? DepositPayable.NDaysFrom
        : DepositPayable.On)
  );

  const onSegmentChange = useCallback((value: string) => {
    const val = value as DepositPayable;

    if (val === DepositPayable.On) {
      setSelectedCondition(Condition.OnDate);
    } else {
      setSelectedCondition(Condition.NdaysFromDate);
    }

    setSelectedSegment(val);

    // make sure to clear any previous selections
    setInspectionOfRecordsDetails(prev => ({
      ...prev,
      depositPayable: val,
      daysFromDate: undefined,
      referenceDate: undefined,
      dayType: DayType.Days,
      specialConditionId: undefined,
    }));
  }, []);

  const isMobile = ScreenSize() === Breakpoints.Mobile;

  const conditionTypeCheck = ConditionType.Contract;

  const handleFromDaysConditionChange = (value: Condition) => {
    setSelectedCondition(value);
    setInspectionOfRecordsDetails(prev => ({
      ...prev,
      condition: value,
      specialConditionId:
        value !== Condition.UponSatisfactionOfSpecialCondition
          ? undefined
          : prev.specialConditionId,
    }));
  };

  useEffect(() => {
    if (!inspectionOfRecordsDetails) return;

    onValueChanged({
      id,
      value: inspectionOfRecordsDetails,
    });
  }, [inspectionOfRecordsDetails]);

  useEffect(() => {
    if (!selectedCondition) return;

    setInspectionOfRecordsDetails(prev => ({
      ...prev,
      condition: selectedCondition,
    }));
  }, [selectedCondition]);

  const inspectionOfRecordsDaysFromOptions = [
    { value: Condition.OnDate, label: 'Contract date' },
  ];

  const filterSelectedOptions = useMemo(
    () =>
      inspectionOfRecordsDaysFromOptions.filter(
        option => option.value !== selectedCondition
      ),
    [inspectionOfRecordsDaysFromOptions, selectedCondition]
  );

  const onSpecialConditionChange = useCallback(
    (specialConditionId?: string) => {
      setInspectionOfRecordsDetails(prev => ({
        ...prev,
        specialConditionId: specialConditionId,
      }));
    },
    []
  );

  return (
    <AgxColumn veryLargeGap fill>
      <AgxColumn mediumGap fill>
        <AgxSegmentPicker
          segments={segments}
          defaultValue={selectedSegment}
          onChange={onSegmentChange}
        />
        {selectedSegment === DepositPayable.On && (
          <AgxDatePicker
            key={`${conditionTypeCheck}OnSpecificDate`}
            id={`${conditionTypeCheck}OnSpecificDate`}
            onChange={value =>
              setInspectionOfRecordsDetails(prev => ({
                ...prev,
                referenceDate: value ?? undefined,
              }))
            }
            required
            noHeader
            value={inspectionOfRecordsDetails.referenceDate}
            stretch
          />
        )}
        {selectedSegment === DepositPayable.NDaysFrom && (
          <AgxRow mediumGap wrap={isMobile} fill>
            <AgxTextInput
              id="numberOfDays"
              numberOnly
              noOptionalLabel
              maxLength={2}
              defaultValue={
                inspectionOfRecordsDetails.daysFromDate?.toString() ?? ''
              }
              extraClasses="depositPayableTextInput"
              onInputValueChange={({ value }) =>
                setInspectionOfRecordsDetails(prev => ({
                  ...prev,
                  daysFromDate: parseStringToNumber(value),
                }))
              }
              error={
                validate && !inspectionOfRecordsDetails.daysFromDate
                  ? 'Enter days'
                  : ''
              }
            />
            <AgxRow>
              <AgxSelect
                options={DAYS_BUSINESSDAYS_OPTIONS}
                id={`ondaysselect-${id}`}
                defaultValue={DAYS_BUSINESSDAYS_OPTIONS.find(
                  option => option.value === inspectionOfRecordsDetails.dayType
                )}
                onValueChanged={({ value }) =>
                  setInspectionOfRecordsDetails(prev => ({
                    ...prev,
                    dayType: value as DayType,
                  }))
                }
                hideOptionalLabel
                required
                small
              />
            </AgxRow>
            <AgxRow centered>
              <AgxLabel small>from</AgxLabel>
            </AgxRow>
            <AgxRow fill>
              <AgxSelect
                options={filterSelectedOptions}
                id={`onselect-${id}`}
                defaultValue={inspectionOfRecordsDaysFromOptions.find(
                  option => option.value === selectedCondition
                )}
                onValueChanged={({ value }) =>
                  handleFromDaysConditionChange(value as Condition)
                }
                disabled
                hideOptionalLabel
                required
                small
              />
            </AgxRow>
          </AgxRow>
        )}
        {selectedCondition === Condition.UponSatisfactionOfSpecialCondition && (
          <AgxSatisfactionOfSpecialCondition
            id={`${conditionTypeCheck}-satisfactionOfSpecialCondition`}
            specialConditions={specialConditions}
            onChange={onSpecialConditionChange}
            campaignId={campaignId}
            selectedClauseId={inspectionOfRecordsDetails?.specialConditionId}
          />
        )}
      </AgxColumn>
    </AgxColumn>
  );
};

export default InspectionOfRecords;
